import { formatMoney } from '@/shared/billing/currency';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { AccessDenied, Button } from '@/presentation/components/common';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { useToast } from '@/presentation/contexts/ToastContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import { formatPeriodLabel } from '@/presentation/components/billing/ClinicInvoiceDocument';

/** Mismo cálculo que /balance/summary, un renglón por período (GET /balance/series). */
export type BalanceSeriesItem = {
  periodo: string;
  facturado: number;
  cobrado: number;
  adeudado: number;
  gastado: number;
  gastadoPendiente: number;
  resultado: number;
  cuentasTotales: number;
  cuentasCobradas: number;
  cuentasAdeudadas: number;
  gastosAprobados: number;
  gastosPendientes: number;
  usd: { facturado: number; cobrado: number; adeudado: number; cuentas: number };
  clinicasActivas: number | null;
  clinicasNuevas: number | null;
  clinicasBajas: number | null;
  fechaInicio: string | null;
  fechaFin: string | null;
  notas: string | null;
};

/** Facturación a mes vencido: el período en cobranza es el último mes cerrado. */
export function collectionPeriod(now = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const pct = (part: number, total: number) => (total > 0 ? (part / total) * 100 : 0);
const ars = (n: number) => formatMoney(n, 'ARS', { decimals: false });
const usdMoney = (n: number) => formatMoney(n, 'USD', { decimals: false });

const csvCell = (value: string | number | null | undefined) => {
  const s = value == null ? '' : String(value);
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function effectivenessClass(value: number): string {
  if (value >= 80) return 'bg-emerald-500';
  if (value >= 60) return 'bg-amber-500';
  return 'bg-rose-500';
}

/**
 * Finanzas → Resumen: un solo tablero con selector de período (el en cobranza por defecto),
 * los totales del período y la evolución mes a mes, todo con el mismo cálculo.
 */
export const FinanceSummaryPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { canAdminister } = useAuth();
  const { error: showError } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [series, setSeries] = useState<BalanceSeriesItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const enCobranza = useMemo(() => collectionPeriod(), []);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await httpClient.get<BalanceSeriesItem[]>('/balance/series');
      setSeries(res.data ?? []);
    } catch (err) {
      showError(err instanceof Error ? err.message : i18next.t('pages.financeSummary.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    if (canAdminister) void load();
  }, [canAdminister, load]);

  const periodOptions = useMemo(() => {
    const set = new Set(series.map((s) => s.periodo));
    set.add(enCobranza);
    return [...set].sort((a, b) => b.localeCompare(a));
  }, [series, enCobranza]);

  const requested = searchParams.get('periodo') ?? '';
  const selected = /^\d{4}-\d{2}$/.test(requested) && periodOptions.includes(requested) ? requested : enCobranza;

  const setSelected = (periodo: string) => {
    const next = new URLSearchParams(searchParams);
    if (periodo === enCobranza) next.delete('periodo');
    else next.set('periodo', periodo);
    setSearchParams(next, { replace: true });
  };

  const current: BalanceSeriesItem | null = useMemo(
    () => series.find((s) => s.periodo === selected) ?? null,
    [series, selected]
  );

  const periodLabel = useCallback(
    (periodo: string) =>
      periodo === enCobranza
        ? `${formatPeriodLabel(periodo, i18n.language)} · ${t('pages.financeSummary.inCollection')}`
        : formatPeriodLabel(periodo, i18n.language),
    [enCobranza, i18n.language, t]
  );

  const exportCsv = () => {
    const header = [
      t('pages.financeSummary.colPeriod'),
      t('pages.financeSummary.billed'),
      t('pages.financeSummary.collected'),
      t('pages.financeSummary.pending'),
      t('pages.financeSummary.collectedPct'),
      t('pages.financeSummary.expenses'),
      t('pages.financeSummary.result'),
      t('pages.financeSummary.billedUsd'),
      t('pages.financeSummary.collectedUsd'),
      t('pages.financeSummary.colActiveClinics'),
    ];
    const lines = series.map((s) =>
      [
        s.periodo,
        s.facturado.toFixed(2),
        s.cobrado.toFixed(2),
        s.adeudado.toFixed(2),
        pct(s.cobrado, s.facturado).toFixed(1),
        s.gastado.toFixed(2),
        s.resultado.toFixed(2),
        s.usd.facturado.toFixed(2),
        s.usd.cobrado.toFixed(2),
        s.clinicasActivas ?? '',
      ]
        .map(csvCell)
        .join(';')
    );
    const blob = new Blob([`﻿${[header.map(csvCell).join(';'), ...lines].join('\n')}`], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${t('pages.financeSummary.fileName')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!canAdminister) return <AccessDenied />;

  const facturado = current?.facturado ?? 0;
  const cobrado = current?.cobrado ?? 0;
  const adeudado = current?.adeudado ?? 0;

  return (
    <div className="panel-page">
      <div className="panel-page-header">
        <div className="min-w-0">
          <h1 className="panel-page-title">{t('pages.financeSummary.title')}</h1>
          <p className="panel-page-subtitle hidden sm:block">{t('pages.financeSummary.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            aria-label={t('pages.financeSummary.period')}
            className="h-9 min-w-[15rem] rounded-md border border-gray-300 bg-white px-2 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            {periodOptions.map((p) => (
              <option key={p} value={p}>
                {periodLabel(p)}
              </option>
            ))}
          </select>
          <Button variant="secondary" size="sm" onClick={() => void load()} disabled={isLoading}>
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      <div className="panel-card overflow-hidden">
        <div className="p-3 xl:p-4">
            {isLoading && series.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-500">{t('common.loading')}</p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2 sm:gap-2.5 md:grid-cols-3 md:gap-3 xl:grid-cols-6">
                  <div className="panel-stat !p-3 border-l-4 border-slate-400">
                    <p className="panel-stat-label">{t('pages.financeSummary.billed')}</p>
                    <p className="panel-stat-value !mt-0.5 text-base sm:text-lg md:text-xl">{ars(facturado)}</p>
                    <p className="text-[11px] text-slate-500">
                      {t('pages.financeSummary.invoicesCount', { count: current?.cuentasTotales ?? 0 })}
                    </p>
                  </div>
                  <div className="panel-stat !p-3 border-l-4 border-green-500">
                    <p className="panel-stat-label">{t('pages.financeSummary.collected')}</p>
                    <p className="panel-stat-value !mt-0.5 text-base text-green-600 dark:text-green-400 sm:text-lg md:text-xl">
                      {ars(cobrado)}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {t('pages.financeSummary.invoicesCount', { count: current?.cuentasCobradas ?? 0 })}
                    </p>
                  </div>
                  <div className="panel-stat !p-3 border-l-4 border-yellow-500">
                    <p className="panel-stat-label">{t('pages.financeSummary.pending')}</p>
                    <p className="panel-stat-value !mt-0.5 text-base text-yellow-600 dark:text-yellow-400 sm:text-lg md:text-xl">
                      {ars(adeudado)}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {t('pages.financeSummary.invoicesCount', { count: current?.cuentasAdeudadas ?? 0 })}
                    </p>
                  </div>
                  <div className="panel-stat !p-3 border-l-4 border-teal-500">
                    <p className="panel-stat-label">{t('pages.financeSummary.collectedPct')}</p>
                    <p className="panel-stat-value !mt-0.5 text-base text-teal-600 dark:text-teal-400 sm:text-lg md:text-xl">
                      {pct(cobrado, facturado).toFixed(1)}%
                    </p>
                  </div>
                  <div className="panel-stat !p-3 border-l-4 border-orange-500">
                    <p className="panel-stat-label">{t('pages.financeSummary.expenses')}</p>
                    <p className="panel-stat-value !mt-0.5 text-base sm:text-lg md:text-xl">{ars(current?.gastado ?? 0)}</p>
                    {current && current.gastosPendientes > 0 ? (
                      <p className="text-[11px] text-slate-500">
                        {t('pages.financeSummary.expensesPending', {
                          count: current.gastosPendientes,
                          amount: ars(current.gastadoPendiente),
                        })}
                      </p>
                    ) : null}
                  </div>
                  <div className="panel-stat !p-3 border-l-4 border-teal-600">
                    <p className="panel-stat-label">{t('pages.financeSummary.result')}</p>
                    <p
                      className={`panel-stat-value !mt-0.5 text-base sm:text-lg md:text-xl ${
                        (current?.resultado ?? 0) >= 0
                          ? 'text-teal-600 dark:text-teal-400'
                          : 'text-rose-600 dark:text-rose-400'
                      }`}
                    >
                      {ars(current?.resultado ?? 0)}
                    </p>
                    <p className="text-[11px] text-slate-500">{t('pages.financeSummary.resultHint')}</p>
                  </div>
                </div>

                {current && current.usd.cuentas > 0 ? (
                  <p className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-gray-900/40 dark:text-slate-300">
                    {t('pages.financeSummary.usdLine', {
                      count: current.usd.cuentas,
                      billed: usdMoney(current.usd.facturado),
                      collected: usdMoney(current.usd.cobrado),
                      pending: usdMoney(current.usd.adeudado),
                    })}
                  </p>
                ) : null}

                {!current ? (
                  <p className="mt-3 text-sm text-slate-500">{t('pages.financeSummary.noInvoices')}</p>
                ) : null}

                <div className="mt-3 flex flex-wrap justify-end gap-x-4 gap-y-1 text-sm font-semibold">
                  <Link
                    to={`/finanzas/facturas?periodo=${encodeURIComponent(selected)}`}
                    className="text-teal-700 hover:underline dark:text-teal-300"
                  >
                    {t('pages.billing.viewPeriodInvoices')} →
                  </Link>
                  <Link to="/finanzas/deudores" className="text-teal-700 hover:underline dark:text-teal-300">
                    {t('pages.financeSummary.viewDebtors')} →
                  </Link>
                  <Link
                    to={`/finanzas/gastos${selected === enCobranza ? '' : `?periodo=${selected}`}`}
                    className="text-teal-700 hover:underline dark:text-teal-300"
                  >
                    {t('pages.financeSummary.viewExpenses')} →
                  </Link>
                </div>
              </>
            )}
          </div>
      </div>

      <div className="panel-card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 dark:border-gray-700">
          <div>
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
              {t('pages.financeSummary.historyTitle')}
            </h2>
            <p className="text-xs text-slate-500">{t('pages.financeSummary.historyHint')}</p>
          </div>
          <Button size="sm" variant="secondary" onClick={exportCsv} disabled={series.length === 0}>
            {t('pages.financeSummary.export')}
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-slate-400">
                <th className="px-4 py-3">{t('pages.financeSummary.colPeriod')}</th>
                <th className="px-4 py-3 text-right">{t('pages.financeSummary.billed')}</th>
                <th className="px-4 py-3 text-right">{t('pages.financeSummary.collected')}</th>
                <th className="px-4 py-3 text-right">{t('pages.financeSummary.pending')}</th>
                <th className="px-4 py-3">{t('pages.financeSummary.collectedPct')}</th>
                <th className="px-4 py-3 text-right">{t('pages.financeSummary.expenses')}</th>
                <th className="px-4 py-3 text-right">{t('pages.financeSummary.result')}</th>
                <th className="px-4 py-3 text-right">{t('pages.financeSummary.colActiveClinics')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
              {series.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    {isLoading ? t('common.loading') : t('pages.financeSummary.noHistory')}
                  </td>
                </tr>
              ) : (
                series.map((s) => {
                  const effectiveness = pct(s.cobrado, s.facturado);
                  const isSelected = s.periodo === selected;
                  return (
                    <tr
                      key={s.periodo}
                      onClick={() => {
                        setSelected(s.periodo);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={`cursor-pointer hover:bg-slate-50/80 dark:hover:bg-gray-900/30 ${
                        isSelected ? 'bg-teal-50/70 dark:bg-teal-900/20' : ''
                      }`}
                    >
                      <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-900 dark:text-white">
                        {periodLabel(s.periodo)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">{ars(s.facturado)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-green-600 dark:text-green-400">
                        {ars(s.cobrado)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-yellow-600 dark:text-yellow-400">
                        {ars(s.adeudado)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-200 dark:bg-gray-700">
                            <div
                              className={`h-full ${effectivenessClass(effectiveness)}`}
                              style={{ width: `${Math.min(100, effectiveness)}%` }}
                            />
                          </div>
                          <span className="tabular-nums text-xs">{effectiveness.toFixed(1)}%</span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">{ars(s.gastado)}</td>
                      <td
                        className={`whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums ${
                          s.resultado >= 0 ? 'text-teal-600 dark:text-teal-400' : 'text-rose-600 dark:text-rose-400'
                        }`}
                      >
                        {ars(s.resultado)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                        {s.clinicasActivas ?? '—'}
                        {s.clinicasActivas != null ? (
                          <span className="ml-1 text-[11px] text-slate-500">
                            +{s.clinicasNuevas ?? 0} / -{s.clinicasBajas ?? 0}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
